import React, { useContext, useMemo } from 'react';
import {
  TransactionsPayGraphTypes,
  AlarmGraphAppNameOptions,
} from '@utils/alarmsConstants';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import { AverageTimeField } from './AverageTimeField/AverageTimeField';
import { TransactionByStatusField } from './TransactionByStatusField/TransactionByStatusField';
import { TransactionHistoryField } from './TransactionHistoryField/TransactionHistoryField';
import { TransactionStatusField } from './TransactionStatusField/TransactionStatusField';
import { TransactionByTypeField } from './TransactionByTypeField/TransactionByTypeField';

export interface ListResponseTransactionsPay {
  success: boolean;
  data?: string[];
}

interface TransactionsPayFieldsProps {
  graphType: string;
  readOnlyFields?: string[];
}

const processData = (data: ListResponseTransactionsPay | undefined) => {
  if (!data) return [];

  const items = Array.isArray(data) ? data : data.data || [];

  return items.map(item => ({
    label: item,
    value: item,
  }));
};

export default function TransactionsPayFields({
  graphType,
  readOnlyFields = [],
}: TransactionsPayFieldsProps) {
  const { customer } = useContext(SessionContext);
  const customerId = customer?.customerLogged?.id;

  const tipoGraficos = {
    averageTime: AlarmGraphAppNameOptions.APPNAME_AVERAGE_TIME,
    transactionByStatus:
      AlarmGraphAppNameOptions.APPNAME_TRANSACTIONS_BY_STATUS,
    transactionHistory: AlarmGraphAppNameOptions.APPNAME_TRANSACTION_HISTORY,
    transactionStatus: AlarmGraphAppNameOptions.APPNAME_TRANSACTION_STATUS,
    transactionByType: AlarmGraphAppNameOptions.APPNAME_TRANSACTIONS_BY_TYPE,
  };

  const { data: averageTimeData } = useSwr<ListResponseTransactionsPay>(
    customerId
      ? `/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task/appName?type=${tipoGraficos.averageTime}`
      : null,
    {
      headers: {
        customerId,
        tipoGrafico: tipoGraficos.averageTime,
      },
    },
  );

  const { data: transactionByStatusData } = useSwr<ListResponseTransactionsPay>(
    customerId
      ? `/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task/appName?type=${tipoGraficos.transactionByStatus}`
      : null,
    {
      headers: {
        customerId,
        tipoGrafico: tipoGraficos.transactionByStatus,
      },
    },
  );

  const { data: transactionHistoryData } = useSwr<ListResponseTransactionsPay>(
    customerId
      ? `/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task/appName?type=${tipoGraficos.transactionHistory}`
      : null,
    {
      headers: {
        customerId,
        tipoGrafico: tipoGraficos.transactionHistory,
      },
    },
  );

  const { data: transactionStatusData } = useSwr<ListResponseTransactionsPay>(
    customerId
      ? `/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task/appName?type=${tipoGraficos.transactionStatus}`
      : null,
    {
      headers: {
        customerId,
        tipoGrafico: tipoGraficos.transactionStatus,
      },
    },
  );

  const { data: transactionByTypeData } = useSwr<ListResponseTransactionsPay>(
    customerId
      ? `/service-proxy/dashboard/transacao/alarme/kapacitor/alerta_task/appName?type=${tipoGraficos.transactionByType}`
      : null,
    {
      headers: {
        customerId,
        tipoGrafico: tipoGraficos.transactionByType,
      },
    },
  );

  const averageTimeOptions = useMemo(
    () => processData(averageTimeData),
    [averageTimeData],
  );
  const transactionByStatusOptions = useMemo(
    () => processData(transactionByStatusData),
    [transactionByStatusData],
  );
  const transactionHistoryOptions = useMemo(
    () => processData(transactionHistoryData),
    [transactionHistoryData],
  );
  const transactionStatusOptions = useMemo(
    () => processData(transactionStatusData),
    [transactionStatusData],
  );
  const transactionByTypeOptions = useMemo(
    () => processData(transactionByTypeData),
    [transactionByTypeData],
  );

  switch (graphType) {
    case TransactionsPayGraphTypes.AVERAGE_TIME:
      return (
        <AverageTimeField
          options={averageTimeOptions}
          loading={!averageTimeData}
          disabled={readOnlyFields.includes('averageTime')}
        />
      );

    case TransactionsPayGraphTypes.TRANSACTIONS_BY_STATUS:
      return (
        <TransactionByStatusField
          options={transactionByStatusOptions}
          loading={!transactionByStatusData}
          disabled={readOnlyFields.includes('transactionByStatus')}
        />
      );

    case TransactionsPayGraphTypes.TRANSACTION_HISTORY:
      return (
        <TransactionHistoryField
          options={transactionHistoryOptions}
          loading={!transactionHistoryData}
          disabled={readOnlyFields.includes('TransactionHistory')}
        />
      );

    case TransactionsPayGraphTypes.TRANSACTION_STATUS:
      return (
        <TransactionStatusField
          options={transactionStatusOptions}
          loading={!transactionStatusData}
          disabled={readOnlyFields.includes('transactionStatus')}
        />
      );

    case TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE:
      return (
        <TransactionByTypeField
          options={transactionByTypeOptions}
          loading={!transactionByTypeData}
          disabled={readOnlyFields.includes('transactionByType')}
        />
      );

    default:
      return null;
  }
}
